import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowDown, BrainCircuit, Waves, BellRing, Droplet,
  Sun, CloudSun, Activity, Sparkles, ArrowUpRight,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Section";
import { IQQuestionnaire } from "@/components/iq/IQQuestionnaire";
import { ASSETS, SAFE_BAND_URL } from "@/lib/assets";

const HERO_FACE =
  "https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/68f7c597525ca4e002651400ecbbf391a99316c9547262004866f3d6b8878006.jpeg";

const STEPS = [
  { icon: BrainCircuit, title: "Understand", body: "Personalized skincare guidance informed by your routine, preferences, and available data." },
  { icon: Waves, title: "Sense", body: "Explore how wearable sensors, activity information, and environmental conditions could provide useful context." },
  { icon: BellRing, title: "Prompt", body: "Receive timely reminders when skincare or protective products may need to be reapplied." },
  { icon: Droplet, title: "Act", body: "Keep your selected liquid, lotion, or gel conveniently accessible through a refillable wearable dispensing platform." },
];

function scrollToRegister() {
  document.getElementById("iq-register")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function IQLanding() {
  const [params] = useSearchParams();
  const source = params.get("src") || params.get("source") || "direct";

  return (
    <div className="min-h-screen bg-white text-black" data-testid="iq-page">
      <SEO
        title="WearSpray IQ: Join the Future of Personalized Skincare"
        description="WearSpray IQ is being developed to connect intelligent sensing, personalized guidance, and immediate access to skincare through our patented wearable dispensing platform. Register your interest in early access."
        path="/iq"
      />

      {/* top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 h-16 md:h-20 border-b border-zinc-100 sticky top-0 bg-white/90 backdrop-blur z-20">
        <Link to="/" data-testid="iq-back-home" className="flex items-center gap-2.5 group">
          <ArrowLeft size={15} className="text-zinc-400 group-hover:text-[#ff2d78] transition-colors" />
          <span className="font-heading text-sm font-medium tracking-tight text-zinc-600 group-hover:text-black transition-colors">
            Wear<span className="text-[#ff2d78]">Spray</span>
          </span>
        </Link>
        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400">IQ</span>
      </div>

      {/* hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#ff2d78]">WearSpray IQ</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mt-4 text-black">
              Join the Future of<br />Personalized Skincare
            </h1>
            <p className="font-body text-lg text-zinc-500 mt-4">Your Personal Skincare Intelligence</p>
            <p className="font-body text-zinc-600 text-base sm:text-lg leading-relaxed mt-6 max-w-lg">
              Imagine having a personal skincare intelligence system that helps you understand your
              environment, recognizes when you may need to reapply your chosen formulation, and
              makes taking action easier.
            </p>
            <p className="font-body text-zinc-600 text-base sm:text-lg leading-relaxed mt-4 max-w-lg">
              WearSpray IQ is being developed to connect intelligent sensing, personalized guidance,
              and immediate access to skincare through our patented wearable dispensing platform.
            </p>
            <p className="font-heading text-lg text-black mt-5 font-medium">We are inviting you to help shape the future.</p>

            <div className="mt-8 flex flex-col items-start gap-3">
              <button
                data-testid="iq-hero-cta"
                onClick={scrollToRegister}
                className="inline-flex items-center gap-2 font-body text-sm sm:text-base font-semibold bg-[#ff2d78] text-white px-8 py-4 sm:py-5 rounded-full hover:bg-black transition-colors shadow-[0_8px_30px_-8px_rgba(255,45,120,0.5)]"
              >
                Join the WearSpray IQ Community <ArrowDown size={16} />
              </button>
              <span className="font-body text-sm text-zinc-500">
                Register your interest in early access and future product evaluation opportunities.
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative mx-auto max-w-sm lg:max-w-none" data-testid="iq-hero-visual">
              <div className="relative rounded-[2rem] overflow-hidden border border-zinc-200 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.25)]">
                <img src={HERO_FACE} alt="Conceptual digital skincare analysis" className="w-full h-full object-cover" />
                {/* scan frame corners */}
                {["top-4 left-4 border-t-2 border-l-2", "top-4 right-4 border-t-2 border-r-2", "bottom-4 left-4 border-b-2 border-l-2", "bottom-4 right-4 border-b-2 border-r-2"].map((pos) => (
                  <span key={pos} className={`absolute ${pos} w-6 h-6 border-[#ff2d78]/70 rounded-sm`} />
                ))}
                {/* floating conceptual data chips */}
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-[18%] right-[6%] bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-lg flex items-center gap-1.5"
                >
                  <Sun size={12} className="text-[#ff2d78]" />
                  <span className="font-mono text-[10px] text-black">UV Index 6</span>
                </motion.div>
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                  className="absolute top-[46%] left-[5%] bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-lg flex items-center gap-1.5"
                >
                  <Activity size={12} className="text-[#ff2d78]" />
                  <span className="font-mono text-[10px] text-black">Hydration 72%</span>
                </motion.div>
                <motion.div
                  animate={{ y: [0, -4, 0] }}
                  transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                  className="absolute bottom-[14%] right-[8%] bg-white/95 backdrop-blur rounded-full px-3 py-1.5 shadow-lg flex items-center gap-1.5"
                >
                  <CloudSun size={12} className="text-[#ff2d78]" />
                  <span className="font-mono text-[10px] text-black">Reapply in ~2h</span>
                </motion.div>
              </div>

              {/* phone mockup, fully code-drawn (no stock device image) */}
              <div className="absolute -bottom-10 -right-6 sm:-right-10 w-[150px] sm:w-[175px] rounded-[1.75rem] bg-black p-2 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)] rotate-3">
                <div className="rounded-[1.4rem] bg-white overflow-hidden">
                  <div className="h-4 flex items-center justify-center bg-black">
                    <span className="w-10 h-1 rounded-full bg-zinc-700" />
                  </div>
                  <div className="p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-[9px] font-semibold text-black">WearSpray IQ</span>
                      <span className="font-mono text-[6px] text-zinc-400 border border-zinc-300 rounded px-1">Conceptual</span>
                    </div>
                    <div className="space-y-1.5 mt-2">
                      <PhoneCard icon={Sun} label="UV Exposure" value="Moderate" />
                      <PhoneCard icon={BellRing} label="Reapply Reminder" value="2:00 PM" />
                      <PhoneCard icon={Activity} label="Biometric Signal" value="Synced" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="font-mono text-[10px] tracking-[0.1em] uppercase text-zinc-400 text-center mt-5">
              Illustrative concept, not a clinically validated measurement
            </p>
          </Reveal>
        </div>
      </section>

      {/* how it works */}
      <section className="bg-zinc-50 border-y border-zinc-100 py-20 md:py-28">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-[#ff2d78]">How WearSpray IQ Works</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mt-4 text-black max-w-xl">
              Four ideas we're exploring, in order
            </h2>
            <p className="font-body text-zinc-500 text-sm mt-3 italic">
              These are planned and proposed capabilities currently under development, not features available today.
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div data-testid={`iq-how-step-${s.title.toLowerCase()}`} className="bg-white border border-zinc-200 rounded-2xl p-6 h-full">
                  <div className="w-11 h-11 rounded-xl bg-[#ff2d78]/10 flex items-center justify-center text-[#ff2d78]">
                    <s.icon size={20} strokeWidth={1.75} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-black mt-5 tracking-wide uppercase">{s.title}</h3>
                  <p className="font-body text-sm text-zinc-500 mt-2.5 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3}>
            <p className="font-heading text-xl sm:text-2xl font-medium text-black mt-12 max-w-2xl leading-snug">
              Most wearables provide information.<br />
              <span className="text-[#ff2d78]">WearSpray aims to connect that information with immediate action.</span>
            </p>
          </Reveal>

          <Reveal delay={0.36}>
            <a
              href={SAFE_BAND_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="iq-shop-safeband-link"
              className="group flex items-center gap-4 mt-10 bg-white border border-zinc-200 rounded-2xl p-5 max-w-xl hover:border-[#ff2d78] transition-colors"
            >
              <img src={ASSETS.fingerPressReservoir} alt="WearSpray wearable dispensing platform" className="w-16 h-16 rounded-xl object-cover shrink-0" />
              <p className="font-body text-sm text-zinc-600 leading-relaxed">
                The real, patented platform behind WearSpray IQ: a refillable wrist dispenser already
                shipping today. The connected intelligence layer above is what we're building next.{" "}
                <span className="inline-flex items-center gap-1 text-[#ff2d78] font-medium group-hover:underline whitespace-nowrap">
                  Shop at SafeBandPro.com <ArrowUpRight size={13} />
                </span>
              </p>
            </a>
          </Reveal>
        </div>
      </section>

      {/* questionnaire */}
      <section id="iq-register" className="py-20 md:py-28 px-6 md:px-10">
        <div className="max-w-xl mx-auto text-center mb-10">
          <Reveal>
            <Sparkles size={28} className="text-[#ff2d78] mx-auto" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight mt-5 text-black">
              Tell Us What You Need
            </h2>
            <p className="font-body text-zinc-500 mt-3">About 2 to 3 minutes. Every question is optional.</p>
          </Reveal>
        </div>
        <IQQuestionnaire source={source} />
      </section>

      <footer className="border-t border-zinc-100 py-8 px-6 md:px-10 text-center">
        <p className="font-body text-xs text-zinc-400 max-w-xl mx-auto leading-relaxed">
          WearSpray IQ features described on this page are planned or proposed capabilities under
          development and are not available in the current shipped product. Example measurements
          shown are conceptual illustrations only.
        </p>
      </footer>
    </div>
  );
}

function PhoneCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between bg-zinc-50 rounded-lg px-2 py-1.5">
      <div className="flex items-center gap-1.5">
        <Icon size={9} className="text-[#ff2d78]" />
        <span className="font-body text-[7px] text-zinc-600">{label}</span>
      </div>
      <span className="font-mono text-[7px] font-medium text-black">{value}</span>
    </div>
  );
}
